/**
 * Listing SEO — the SINGLE source of truth for the public ad page's SEO payload
 * (`/advertisements/property/:id`): clean headline, REGA display price/type,
 * title, meta description, og:image and the JSON-LD (BreadcrumbList +
 * RealEstateListing). Pure + dependency-free + Arabic-primary.
 *
 * Consumers:
 *   - SPA: `src/lib/listingSeo.ts`, `src/lib/regaPricing.ts`,
 *     `src/lib/listingTitle.ts`, `src/lib/listingDisplay.ts` re-export / wrap
 *     these; `PropertyDetails` builds its <head> from `buildPublicListingSeo`.
 *   - API: `apps/api/src/listings/seo/listing-seo.mirror.ts` is a VERBATIM copy
 *     of everything below the BEGIN marker (apps/api may not runtime-import
 *     @dallali/shared — tools/ci/check-api-shared-import.mjs). Its spec fails
 *     if the two bodies differ by a single character, and runs both on the same
 *     fixtures — so the server-rendered page (crawlers / link previews) emits
 *     the SAME JSON-LD as the SPA.
 *
 * Keep this file import-free and compatible with both tsconfigs (SPA strict,
 * API noImplicitAny/strictNullChecks). After editing, copy the body into the
 * API mirror (the parity spec tells you if you forgot).
 */
// ── BEGIN SHARED LISTING-SEO BODY ──

// ── Numbers ──────────────────────────────────────────────────────────────────

/** Thousands-grouped number with ≤ 2 decimals ("1,250,000"). */
export function formatListingNumber(n: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
}

/** Parse a REGA number that may arrive as a string ("147.59", "1,200"). */
export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value.replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Area to show with the listing's price: the REGA (deed) area wins and the
 * unit's own area is the fallback — except when the deed covers a building of
 * several units. There the deed area is the whole building's while the price
 * is one unit's, so the unit's own area is used and price and area stay a
 * pair. `fromDeed` drives the "(حسب الصك)" label.
 */
export function pickListingArea(
  rega: { propertyArea?: unknown } | null | undefined,
  unitArea: number | null | undefined,
  deedCoversMultipleUnits = false,
): { value: number; fromDeed: boolean } | null {
  const deed = toFiniteNumber(rega?.propertyArea);
  const unit = toFiniteNumber(unitArea);
  const hasUnit = unit !== null && unit > 0;
  if (deed !== null && deed > 0 && !(deedCoversMultipleUnits && hasUnit)) return { value: deed, fromDeed: true };
  if (hasUnit) return { value: unit, fromDeed: false };
  return null;
}

// Properties write region/street into the `amenities` JSONB column under
// `information.region` / `information.streetName` (see AddPropertyDialog).
export function extractPropertyInfo(amenities: unknown): {
  region?: string;
  streetName?: string;
  shortAddress?: string;
} {
  if (!amenities || typeof amenities !== "object") return {};
  const info = (amenities as { information?: unknown }).information;
  if (!info || typeof info !== "object") return {};
  const i = info as Record<string, unknown>;
  return {
    region: typeof i.region === "string" ? i.region : undefined,
    streetName: typeof i.streetName === "string" ? i.streetName : undefined,
    shortAddress: typeof i.shortAddress === "string" ? i.shortAddress : undefined,
  };
}

// ── REGA pricing ─────────────────────────────────────────────────────────────

/**
 * Minimal subset of REGA fields needed to compute display prices.
 * RegaDisplayData and RegaValidateResponse both satisfy this shape.
 */
export interface RegaPriceFields {
  propertyPrice?: string | number;
  propertyArea?: string | number;
  propertyType?: string;
  advertisementType?: string;
  landTotalPrice?: number | string | null;
  landTotalAnnualRent?: number | string | null;
}

/**
 * REGA prices some ads PER SQUARE METER: `propertyPrice` is the m² rate and the
 * real figure is `propertyPrice × propertyArea` (REGA sends the precomputed
 * total in `landTotalPrice` / `landTotalAnnualRent` for land ads).
 *
 * This set is EVIDENCE-BASED — extend it only with a verified real ad, never by
 * analogy. A wrong entry inflates a total-priced ad by the area factor.
 * - Land (ارض/أرض/land): proven in PR #1524 against real prod ads.
 * - School (مدرسة): proven 2026-07-12 (CEO) — Unayzah school ad.
 * Deliberately NOT included: شقة/فيلا (real ads proved `propertyPrice` is the
 * total) and all other facility types (unproven). The Arabic root `مدرس` is
 * intentional (REGA spelling drift; `مدرس` alone is not a REGA type).
 */
const PARCEL_PRICED_TYPE = /[اأإآ]ر?ض|land|مدرس|school/i;

export function isParcelPricedType(propertyType: string | null | undefined): boolean {
  return PARCEL_PRICED_TYPE.test(String(propertyType ?? ""));
}

function toPositiveNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Total price for a per-m²-capable REGA figure: prefer REGA's own total when
 * present, multiply rate × area for parcel-priced types, else the raw figure
 * is already the total. Returns 0 for null/absent/garbage input so callers can
 * `|| listing.price` their fallback.
 */
function resolveRegaTotal(
  rega: RegaPriceFields,
  regaTotal: number | string | null | undefined,
): number {
  const total = toPositiveNumber(regaTotal);
  if (total > 0) return total;
  const price = toPositiveNumber(rega.propertyPrice);
  const area = toPositiveNumber(rega.propertyArea);
  if (isParcelPricedType(rega.propertyType)) {
    // Parcel-priced with no usable area → underivable; 0 → caller falls back.
    return area > 0 ? price * area : 0;
  }
  return price;
}

/** Correct total SALE price for a REGA listing (see PARCEL_PRICED_TYPE). */
export function getRegaSaleTotal(rega: RegaPriceFields | null | undefined): number {
  if (!rega) return 0;
  return resolveRegaTotal(rega, rega.landTotalPrice);
}

/** Correct ANNUAL rent for a REGA listing — same per-m² semantics. */
export function getRegaAnnualRent(rega: RegaPriceFields | null | undefined): number {
  if (!rega) return 0;
  return resolveRegaTotal(rega, rega.landTotalAnnualRent);
}

/** REGA advertisementType → rent/sale (anything that isn't a sale is rent). */
export function regaAdTypeToListingType(regaAdType: string): "rent" | "sale" {
  if (!regaAdType || typeof regaAdType !== "string") return "rent";
  const lower = regaAdType.toLowerCase();
  if (lower.includes("بيع") || lower.includes("sell") || lower.includes("sale")) return "sale";
  return "rent";
}

/**
 * The rent/sale type the ad must display: REGA's validated `advertisementType`
 * wins over whatever was stored on the listing row (the row can drift). Falls
 * back to the stored type when REGA data is absent.
 */
export function getRegaEffectiveListingType(
  rega: Pick<RegaPriceFields, "advertisementType"> | null | undefined,
  fallback: "rent" | "sale",
): "rent" | "sale" {
  const adType = rega?.advertisementType;
  if (!adType || typeof adType !== "string") return fallback;
  return regaAdTypeToListingType(adType);
}

/**
 * The price a public surface should display for a REGA-backed listing:
 * sale → total sale price, rent → annual rent. 0 when REGA data is
 * absent/garbage so callers can fall back to the stored `listing.price`.
 */
export function getRegaDisplayPrice(
  rega: RegaPriceFields | null | undefined,
  effectiveType: "rent" | "sale",
): number {
  if (!rega) return 0;
  return effectiveType === "sale" ? getRegaSaleTotal(rega) : getRegaAnnualRent(rega);
}

// ── Headline ─────────────────────────────────────────────────────────────────

export interface ListingHeadlineInput {
  unit_type?: string | null;
  /** Used when unit_type is absent (e.g. the REGA propertyType label). */
  type_label?: string | null;
  listing_type?: "rent" | "sale" | string | null;
  district?: string | null;
  city?: string | null;
}

/**
 * Catalog lookups the headline needs. The SPA plugs in its unit-type matrix +
 * Saudi city/district catalogs; a consumer without the catalogs returns
 * `undefined` from cityName/districtName and gets the documented fallbacks.
 */
export interface ListingHeadlineResolvers {
  /** Label for a unit_type value (the matrix returns the raw value when unknown). */
  unitTypeLabel: (unitType: string, language: "ar" | "en") => string;
  /** Catalog display name for a stored city, or undefined when not in the catalog. */
  cityName: (city: string, language: "ar" | "en") => string | undefined;
  /** Catalog display name for a trimmed stored district, or undefined. */
  districtName: (district: string, language: "ar" | "en") => string | undefined;
}

function purposeLabel(listingType: string | null | undefined, language: "ar" | "en"): string {
  if (listingType === "rent") return language === "ar" ? "للإيجار" : "For Rent";
  if (listingType === "sale") return language === "ar" ? "للبيع" : "For Sale";
  return "";
}

/**
 * Short, clean headline for the public ad page H1 / <title>:
 *   "<Type> <Purpose> في <حي District>، <City>"
 * e.g. "شقة للإيجار في حي الملقا، الرياض" / "Apartment For Rent in Al Malqa, Riyadh".
 * Never includes the street/national-address string or the region.
 */
export function composeListingHeadline(
  input: ListingHeadlineInput,
  language: "ar" | "en",
  resolvers: ListingHeadlineResolvers,
): string {
  const type =
    (input.unit_type ? resolvers.unitTypeLabel(input.unit_type, language) : "") ||
    (input.type_label ?? "").trim();
  const lead = [type, purposeLabel(input.listing_type, language)].filter(Boolean).join(" ");

  const cityCatalog = input.city ? resolvers.cityName(input.city, language) : undefined;
  const city = cityCatalog ?? (input.city ?? "").trim();

  let district = "";
  const rawDistrict = (input.district ?? "").trim();
  if (rawDistrict) {
    const catalog = resolvers.districtName(rawDistrict, language);
    if (catalog !== undefined) district = catalog;
    else if (language === "ar") district = rawDistrict.startsWith("حي") ? rawDistrict : `حي ${rawDistrict}`;
    else district = rawDistrict;
  }

  const place = [district, city].filter(Boolean).join(language === "ar" ? "، " : ", ");
  if (lead && place) return `${lead} ${language === "ar" ? "في" : "in"} ${place}`;
  return lead || place;
}

// ── Public listing → SEO inputs ──────────────────────────────────────────────

interface RegaLocationLike {
  city?: string;
  region?: string;
  district?: string;
  street?: string;
  buildingNumber?: string;
  postalCode?: string;
  latitude?: number | null;
  longitude?: number | null;
}

/** The REGA payload fields the SEO pipeline reads (rega_display_data JSONB). */
export interface RegaSeoFields extends RegaPriceFields {
  numberOfRooms?: unknown;
  location?: RegaLocationLike;
}

/**
 * Structural subset of the public listing detail (GET /listings/public/:id)
 * that the SEO pipeline reads. Both the SPA's listing object and the API's
 * WireListingPublicDetail satisfy it.
 */
export interface PublicListingSeoSource {
  id: string;
  title: string;
  description?: string | null;
  price?: number | null;
  listing_type?: string | null;
  created_at?: string | null;
  image_urls?: readonly string[] | null;
  rega_display_data?: unknown;
  property?: {
    city?: string | null;
    district?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    amenities?: unknown;
    /** Units in the property, declared or registered, whichever is larger (public payloads carry it). */
    units_count?: number | null;
  } | null;
  unit?: {
    unit_type?: string | null;
    bedrooms?: number | null;
    bathrooms?: number | null;
    area?: number | null;
    images?: unknown;
  } | null;
}

// ── Public URLs: readable slug + short id (website fix spec P1-2) ───────────
//
// /advertisements/office-for-rent-in-ar-rabie-riyadh-fa37876f
// /projects/verva-park-f8413b60
// The words are for people and search engines; the trailing 8 hex digits (the
// start of the row's UUID) are what the server looks up. Changing a listing's
// district or a project's name changes its slug, and the old slug still
// resolves through the short id — the page then canonicalises to the new one.

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** First 8 hex digits of a UUID, lowercase. */
export function shortIdOf(id: string): string {
  return id.slice(0, 8).toLowerCase();
}

/** Lowercase, hyphenated, Latin/Arabic letters and digits only, ≤ 80 chars. */
export function slugify(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[̀-ًͯ-ٰٟ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9ء-ي]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/, "");
}

/** `<slug>-<shortid>`, or just the short id when the text has no usable characters. */
export function publicSlug(text: string | null | undefined, id: string): string {
  const slug = slugify(text ?? "");
  return slug ? `${slug}-${shortIdOf(id)}` : shortIdOf(id);
}

/**
 * The short id a URL segment points at: a full UUID (old URLs) or a slug
 * ending in `-<8 hex>` / a bare 8-hex id. Null for anything else.
 */
export function shortIdFromParam(param: string | null | undefined): string | null {
  const p = (param ?? "").trim().toLowerCase();
  if (UUID_SHAPE.test(p)) return p.slice(0, 8);
  return p.match(/(?:^|-)([0-9a-f]{8})$/)?.[1] ?? null;
}

/** Root (Arabic) path of a public ad page for slug words `slugText` (see listingPathOf). */
export function listingCanonicalPath(id: string, slugText: string): string {
  return `/advertisements/${publicSlug(slugText, id)}`;
}

/** Root (Arabic) path of a public project (property) page; prefer the English name. */
export function projectCanonicalPath(id: string, name: string | null | undefined): string {
  return `/projects/${publicSlug(name, id)}`;
}

/** The listing's REGA payload, or null when absent / not an object. */
export function regaSeoFieldsOf(listing: PublicListingSeoSource): RegaSeoFields | null {
  const r = listing.rega_display_data;
  return r && typeof r === "object" ? (r as RegaSeoFields) : null;
}

/**
 * Root (Arabic) path of a listing's public page — the one place its slug is
 * built, e.g. /advertisements/office-for-rent-الربيع-الرياض-fa37876f.
 * The words come straight from the stored data (unit type code, rent/sale,
 * district, city) with no label catalog: the SPA and the API resolve names
 * through different catalogs, and both must produce the same URL.
 */
export function listingPathOf(listing: PublicListingSeoSource): string {
  const input = listingHeadlineInputOf(listing, null);
  const words = [
    (input.unit_type ?? "").replace(/_/g, " "),
    input.listing_type === "sale" ? "for sale" : "for rent",
    input.district,
    input.city,
  ];
  return listingCanonicalPath(listing.id, words.filter(Boolean).join(" "));
}

/** The listing's display area (see pickListingArea) — one rule for card, page and prerender. */
export function listingAreaOf(listing: PublicListingSeoSource): { value: number; fromDeed: boolean } | null {
  return pickListingArea(regaSeoFieldsOf(listing), listing.unit?.area, (listing.property?.units_count ?? 0) > 1);
}

/** REGA-effective rent/sale type for a public listing (row type as fallback). */
export function effectiveListingTypeOf(listing: PublicListingSeoSource): "rent" | "sale" {
  return getRegaEffectiveListingType(
    regaSeoFieldsOf(listing),
    (listing.listing_type ?? "rent") as "rent" | "sale",
  );
}

/** Price to display: REGA-derived total/annual, else the stored listing price. */
export function displayPriceOf(listing: PublicListingSeoSource): number | null {
  return getRegaDisplayPrice(regaSeoFieldsOf(listing), effectiveListingTypeOf(listing)) || listing.price || null;
}

/** Gallery order: PROPERTY images (image_urls) first, then UNIT images, deduped. */
export function listingImagesOf(listing: PublicListingSeoSource): string[] {
  const unitImages = Array.isArray(listing.unit?.images)
    ? (listing.unit?.images as unknown[]).filter((s): s is string => typeof s === "string")
    : [];
  return Array.from(new Set([...(listing.image_urls ?? []), ...unitImages]));
}

/** Headline input for a public listing; `typeLabel` = the REGA propertyType in the target language. */
export function listingHeadlineInputOf(
  listing: PublicListingSeoSource,
  typeLabel: string | null | undefined,
): ListingHeadlineInput {
  const loc = regaSeoFieldsOf(listing)?.location;
  return {
    unit_type: listing.unit?.unit_type,
    type_label: typeLabel,
    listing_type: effectiveListingTypeOf(listing),
    district: listing.property?.district || loc?.district,
    city: listing.property?.city || loc?.city,
  };
}

// ── SEO payload ──────────────────────────────────────────────────────────────

export interface ListingSeoInput {
  siteUrl: string;
  canonicalPath: string;
  /** Clean Arabic headline (composeListingHeadline(…, "ar")). */
  headlineAr: string;
  unitType?: string | null;
  listingType: "rent" | "sale";
  cityAr?: string | null;
  regionAr?: string | null;
  districtAr?: string | null;
  streetAddress?: string | null;
  postalCode?: string | null;
  price?: number | null;
  /** m² — deed (REGA) area preferred by the caller. */
  area?: number | null;
  rooms?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  description?: string | null;
  images: string[];
  datePosted?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface ListingSeo {
  title: string;
  description: string;
  ogImage?: string;
  jsonLd: Record<string, unknown>[];
}

/** Map a public listing onto the SEO builder input (exactly what the ad page uses). */
export function listingSeoInputOf(
  listing: PublicListingSeoSource,
  ctx: { siteUrl: string; headlineAr: string },
): ListingSeoInput {
  const rega = regaSeoFieldsOf(listing);
  const loc = rega?.location;
  const info = extractPropertyInfo(listing.property?.amenities);
  return {
    siteUrl: ctx.siteUrl,
    canonicalPath: listingPathOf(listing),
    headlineAr: ctx.headlineAr || listing.title,
    unitType: listing.unit?.unit_type,
    listingType: effectiveListingTypeOf(listing),
    cityAr: loc?.city || listing.property?.city,
    regionAr: loc?.region || info.region,
    districtAr: loc?.district || listing.property?.district,
    streetAddress: [loc?.buildingNumber, loc?.street || info.streetName].filter(Boolean).join(" ") || null,
    postalCode: loc?.postalCode,
    price: displayPriceOf(listing),
    area: listingAreaOf(listing)?.value ?? null,
    rooms: toFiniteNumber(rega?.numberOfRooms),
    bedrooms: listing.unit?.bedrooms ?? null,
    bathrooms: listing.unit?.bathrooms ?? null,
    description: listing.description,
    images: listingImagesOf(listing),
    datePosted: listing.created_at,
    latitude: listing.property?.latitude ?? loc?.latitude ?? null,
    longitude: listing.property?.longitude ?? loc?.longitude ?? null,
  };
}

const MAX_DESCRIPTION = 160;

/** schema.org type for the thing being listed. */
export function accommodationTypeFor(unitType: string | null | undefined): string {
  switch (unitType) {
    case "apartment":
    case "studio":
    case "duplex":
    case "penthouse":
      return "Apartment";
    case "villa":
      return "SingleFamilyResidence";
    case "house":
      return "House";
    case "land":
      return "Place";
    default:
      return "Accommodation";
  }
}

/** Absolute http(s) image URLs only — data:/blob: URIs are useless to crawlers. */
export function absoluteImageUrls(images: readonly string[], siteUrl: string): string[] {
  return images
    .map((src) => (src.startsWith("/") ? `${siteUrl}${src}` : src))
    .filter((src) => /^https?:\/\//i.test(src));
}

function clampDescription(base: string, extra: string | null | undefined): string {
  let out = base;
  const text = (extra ?? "").replace(/\s+/g, " ").trim();
  const room = MAX_DESCRIPTION - out.length - 1;
  if (text && room > 20) {
    if (text.length <= room) {
      out = `${out} ${text}`;
    } else {
      const cut = text.slice(0, room - 1);
      const lastSpace = cut.lastIndexOf(" ");
      out = `${out} ${(lastSpace > 10 ? cut.slice(0, lastSpace) : cut).replace(/[،,.\s]+$/, "")}…`;
    }
  }
  return out.length > MAX_DESCRIPTION ? `${out.slice(0, MAX_DESCRIPTION - 1)}…` : out;
}

export function buildListingSeo(input: ListingSeoInput): ListingSeo {
  const canonicalUrl = `${input.siteUrl}${input.canonicalPath}`;
  const isRent = input.listingType === "rent";

  const facts: string[] = [];
  if (input.price) facts.push(`بسعر ${formatListingNumber(input.price)} ريال${isRent ? " سنوياً" : ""}`);
  const rooms = input.bedrooms || input.rooms;
  if (rooms) facts.push(`${rooms} غرف`);
  if (input.bathrooms) facts.push(`${input.bathrooms} دورات مياه`);
  if (input.area) facts.push(`بمساحة ${formatListingNumber(input.area)} م²`);
  const base = `${input.headlineAr}${facts.length ? `، ${facts.join("، ")}` : ""}.`;
  const description = clampDescription(base, input.description);

  const images = absoluteImageUrls(input.images, input.siteUrl);
  const hasGeo = input.latitude != null && input.longitude != null;

  const address: Record<string, unknown> = {
    "@type": "PostalAddress",
    ...(input.streetAddress ? { streetAddress: input.streetAddress } : {}),
    ...(input.cityAr ? { addressLocality: input.cityAr } : {}),
    ...(input.regionAr ? { addressRegion: input.regionAr } : {}),
    ...(input.postalCode ? { postalCode: input.postalCode } : {}),
    addressCountry: "SA",
  };

  const about: Record<string, unknown> = {
    "@type": accommodationTypeFor(input.unitType),
    name: input.headlineAr,
    ...(input.area ? { floorSize: { "@type": "QuantitativeValue", value: input.area, unitCode: "MTK" } } : {}),
    ...(input.rooms ? { numberOfRooms: input.rooms } : {}),
    ...(input.bedrooms ? { numberOfBedrooms: input.bedrooms } : {}),
    ...(input.bathrooms ? { numberOfBathroomsTotal: input.bathrooms } : {}),
    address,
    ...(hasGeo
      ? { geo: { "@type": "GeoCoordinates", latitude: input.latitude, longitude: input.longitude } }
      : {}),
  };

  const offers = input.price
    ? {
        "@type": "Offer",
        price: input.price,
        priceCurrency: "SAR",
        availability: "https://schema.org/InStock",
        businessFunction: isRent
          ? "http://purl.org/goodrelations/v1#LeaseOut"
          : "http://purl.org/goodrelations/v1#Sell",
        ...(isRent
          ? {
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: input.price,
                priceCurrency: "SAR",
                unitCode: "ANN",
                unitText: "year",
              },
            }
          : {}),
      }
    : undefined;

  const jsonLd: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "الرئيسية", item: `${input.siteUrl}/home` },
        { "@type": "ListItem", position: 2, name: "الإعلانات", item: `${input.siteUrl}/advertisements` },
        { "@type": "ListItem", position: 3, name: input.headlineAr, item: canonicalUrl },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: input.headlineAr,
      description,
      url: canonicalUrl,
      ...(images.length ? { image: images } : {}),
      ...(input.datePosted ? { datePosted: input.datePosted } : {}),
      ...(offers ? { offers } : {}),
      about,
    },
  ];

  return { title: `${input.headlineAr} | دلّالي`, description, ogImage: images[0], jsonLd };
}

/** One call: public listing → SEO payload (what the SPA <head> and the API prerender emit). */
export function buildPublicListingSeo(
  listing: PublicListingSeoSource,
  ctx: { siteUrl: string; headlineAr: string },
): ListingSeo {
  return buildListingSeo(listingSeoInputOf(listing, ctx));
}

// ── Public project (property) page SEO (website fix spec P1-3) ──────────────

/** The project page fields the SEO builder reads (public property payload). */
export interface PublicProjectSeoSource {
  id: string;
  name: string;
  name_en?: string | null;
  description?: string | null;
  description_en?: string | null;
  property_type?: string | null;
  purpose?: string | null;
  address?: string | null;
  city?: string | null;
  district?: string | null;
  postal_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  images?: readonly string[] | null;
}

export interface ProjectSeo {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  jsonLd: Record<string, unknown>[];
}

const RESIDENTIAL_PROJECT_TYPES = new Set(["residential", "compound", "apartment"]);
/** Spec P1-3: the first 155 characters of the project description. */
const PROJECT_DESCRIPTION_MAX = 155;

/** Emoji and pictographs out, whitespace collapsed (meta descriptions stay plain text). */
export function stripEmoji(text: string): string {
  return text
    .replace(/\p{Extended_Pictographic}|\p{Regional_Indicator}|\u{FE0F}|\u{200D}|\u{20E3}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** First `max` characters, cut back to a word boundary with an ellipsis when it had to cut. */
function truncateAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * Title, description, canonical, og:image and JSON-LD for a public project
 * page. One builder for the SPA <head> and the API prerender.
 *   title: "{name} | {district}، {city} | دلّالي" (English: "{name} | {district}, {city} | Dallali")
 *   description: the project's own description, emoji stripped, ≤ 155 chars
 *   og:image: the first project photo
 *   JSON-LD: ApartmentComplex (residential) or Place, plus BreadcrumbList
 */
export function buildProjectSeo(
  property: PublicProjectSeoSource,
  ctx: { siteUrl: string; language: "ar" | "en" },
): ProjectSeo {
  const en = ctx.language === "en";
  const name = ((en ? property.name_en : null) || property.name || "").trim();
  const place = [property.district, property.city].map((s) => (s ?? "").trim()).filter(Boolean).join(en ? ", " : "، ");
  const title = [name, place, en ? "Dallali" : "دلّالي"].filter(Boolean).join(" | ");

  const ownText = stripEmoji((en ? property.description_en : null) || property.description || "");
  const fallback = en
    ? `${name}${place ? ` in ${place}` : ""}: units for rent and sale on Dallali.`
    : `${name}${place ? ` في ${place}` : ""}: وحدات للإيجار والبيع عبر دلّالي.`;
  const description = truncateAtWord(ownText || fallback, PROJECT_DESCRIPTION_MAX);

  const canonicalPath = projectCanonicalPath(property.id, property.name_en || property.name);
  const pagePath = `${en ? "/en" : ""}${canonicalPath}`;
  const url = `${ctx.siteUrl}${pagePath}`;
  const images = absoluteImageUrls(property.images ?? [], ctx.siteUrl);

  const residential =
    RESIDENTIAL_PROJECT_TYPES.has(property.property_type ?? "") ||
    (["building", "mixed"].includes(property.property_type ?? "") && property.purpose === "residential");
  const placeLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": residential ? "ApartmentComplex" : "Place",
    name,
    url,
    ...(ownText ? { description: truncateAtWord(ownText, 500) } : {}),
    ...(images.length ? { image: images.slice(0, 6) } : {}),
    address: {
      "@type": "PostalAddress",
      ...(property.address ? { streetAddress: property.address } : {}),
      ...(property.district || property.city
        ? { addressLocality: [property.district, property.city].filter(Boolean).join(en ? ", " : "، ") }
        : {}),
      ...(property.postal_code ? { postalCode: property.postal_code } : {}),
      addressCountry: "SA",
    },
    ...(property.latitude != null && property.longitude != null
      ? { geo: { "@type": "GeoCoordinates", latitude: property.latitude, longitude: property.longitude } }
      : {}),
  };
  const breadcrumb: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: en ? "Home" : "الرئيسية", item: `${ctx.siteUrl}${en ? "/en" : "/"}` },
      { "@type": "ListItem", position: 2, name: en ? "Listings" : "الإعلانات", item: `${ctx.siteUrl}${en ? "/en" : ""}/advertisements` },
      { "@type": "ListItem", position: 3, name, item: url },
    ],
  };

  return { title, description, canonicalPath, ogImage: images[0], jsonLd: [placeLd, breadcrumb] };
}
